import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Box,
  Typography,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
  Avatar,
  Popover,
  Paper,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  FaChartBar,
  FaUsersCog,
  FaGlobe,
  FaFileContract,
  FaUsers,
  FaBrain,
  FaWater,
  FaBuilding,
  FaUserCircle,
  FaSignOutAlt,
  FaTasks,
  FaPlus,
} from 'react-icons/fa';
import { 
  ChevronLeft, 
  ChevronRight, 
  ExpandMore, 
  ExpandLess,
  SwapHoriz,
  Add,
  Edit,
} from '@mui/icons-material';
import { getAuth, signOut } from 'firebase/auth';
import axios from 'axios';
import { toast } from 'react-toastify';
import { usePermissions } from '../contexts/PermissionContext';
import { useOrg } from '../contexts/OrgContext';
import BASE_URL from '../config';
import { ROLES } from '../constants/roles';

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 70;

function ModernSidebar({ isCollapsed, toggleSidebar, onClose, variant = 'permanent' }) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { role, can, permissions, loading, isOrgAdmin } = usePermissions();
  const { selectedOrgId, selectedOrgName, updateSelectedOrg, allOrganisations, isSuperAdmin } = useOrg();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Organization menu state
  const [orgMenuAnchor, setOrgMenuAnchor] = useState(null);
  const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgLogo, setNewOrgLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [creatingOrg, setCreatingOrg] = useState(false);
  
  // Edit logo dialog state
  const [editLogoDialogOpen, setEditLogoDialogOpen] = useState(false);
  const [editLogoFile, setEditLogoFile] = useState(null);
  const [editLogoPreview, setEditLogoPreview] = useState(null);
  const [updatingLogo, setUpdatingLogo] = useState(false);
  const [currentOrgLogo, setCurrentOrgLogo] = useState(null);
  
  // Organisation logo URL state
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);
  
  // Load organisation logo when org changes
  useEffect(() => {
    if (selectedOrgId) {
      // Set the logo URL - the img element will handle 404s gracefully
      setOrgLogoUrl(`${BASE_URL}/api/organisations/${selectedOrgId}/logo?t=${Date.now()}`);
    } else {
      setOrgLogoUrl(null);
    }
  }, [selectedOrgId]);



  // Submenu open states
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [siteOpen, setSiteOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [ecoAssetOpen, setEcoAssetOpen] = useState(false);
  const [lakeOpen, setLakeOpen] = useState(false);

  // Popover state for collapsed sidebar
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverContent, setPopoverContent] = useState(null);

  // Organization handlers for superadmin
  const handleOrgMenuOpen = (event) => {
    if (isSuperAdmin && allOrganisations.length > 0) {
      setOrgMenuAnchor(event.currentTarget);
    }
  };

  const handleOrgMenuClose = () => {
    setOrgMenuAnchor(null);
  };

  const handleOrgSwitch = (org) => {
    updateSelectedOrg(org.id, org.name);
    handleOrgMenuClose();
    toast.success(`Switched to ${org.name}`);
    // Refresh the page to load new org data
    window.location.reload();
  };

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) {
      toast.error('Organization name is required');
      return;
    }

    setCreatingOrg(true);
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();

      // Use FormData to support file upload
      const formData = new FormData();
      formData.append('name', newOrgName.trim());
      if (newOrgLogo) {
        formData.append('logo', newOrgLogo);
      }

      const response = await axios.post(
        `${BASE_URL}/api/new_organizations`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      toast.success(`Organization "${newOrgName}" created successfully!`);
      setCreateOrgDialogOpen(false);
      setNewOrgName('');
      setNewOrgLogo(null);
      setLogoPreview(null);
      
      // Switch to the new organization
      if (response.data?.organisation) {
        updateSelectedOrg(response.data.organisation.id, response.data.organisation.name);
        window.location.reload();
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error(error.response?.data?.error || 'Failed to create organization');
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file size must be less than 2MB');
        return;
      }
      setNewOrgLogo(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setNewOrgLogo(null);
    setLogoPreview(null);
  };

  // Fetch current org logo when edit dialog opens
  const handleOpenEditLogoDialog = async () => {
    setEditLogoDialogOpen(true);
    setEditLogoFile(null);
    setEditLogoPreview(null);
    
    // Try to load current logo
    if (selectedOrgId) {
      try {
        const logoUrl = `${BASE_URL}/api/organisations/${selectedOrgId}/logo?t=${Date.now()}`;
        setCurrentOrgLogo(logoUrl);
      } catch (error) {
        setCurrentOrgLogo(null);
      }
    }
  };

  const handleEditLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file size must be less than 2MB');
        return;
      }
      setEditLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateLogo = async (removeLogo = false) => {
    setUpdatingLogo(true);
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();

      const formData = new FormData();
      if (!removeLogo && editLogoFile) {
        formData.append('logo', editLogoFile);
      }
      // If removeLogo is true, we send empty formData which will set logo to null

      await axios.put(
        `${BASE_URL}/api/organisations/${selectedOrgId}/logo`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      toast.success(removeLogo ? 'Logo removed successfully!' : 'Logo updated successfully!');
      setEditLogoDialogOpen(false);
      setEditLogoFile(null);
      setEditLogoPreview(null);
      setCurrentOrgLogo(null);
      // Force refresh to update the logo display
      window.location.reload();
    } catch (error) {
      console.error('Error updating logo:', error);
      toast.error(error.response?.data?.error || 'Failed to update logo');
    } finally {
      setUpdatingLogo(false);
    }
  };

  // Auto-expand based on current path
  useEffect(() => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/lakedashboard') setDashboardOpen(true);
    if (['/users', '/userpermission', '/departments', '/roles', '/usergroups'].includes(path)) {
      setAdminOpen(true);
    }
    if (path === '/sites') setSiteOpen(true);
    if (['/contractor', '/agreement', '/contracttype'].includes(path)) setContractOpen(true);
    if (['/taskmanagement', '/maintenance', '/workflows', '/sla'].includes(path)) setTaskOpen(true);
    if (['/ecological', '/lake'].includes(path)) setEcoAssetOpen(true);
  }, [location.pathname]);

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        navigate('/login');
      })
      .catch((error) => {
        console.error('Logout error:', error);
      });
  };

  const handlePopoverOpen = (event, item) => {
    if (isCollapsed && item.children && item.children.length > 0) {
      setPopoverAnchor(event.currentTarget);
      setPopoverContent(item);
    }
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setPopoverContent(null);
  };

  // Contractor role: web sidebar is scoped to Tasks only (mobile is the primary
  // surface for vendor crew; see VENDOR_MANAGEMENT_PLAN.md 3.4).
  const isContractor = role === ROLES.CONTRACTOR;

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <FaChartBar />,
      open: dashboardOpen,
      setOpen: setDashboardOpen,
      permission: !isContractor,
      children: [
        { label: 'Eco Dashboard', path: '/dashboard', permission: true },
        { label: 'Task Dashboard', path: '/taskdashboard', permission: true },
        { label: 'Lake Dashboard', path: '/lakedashboard', permission: can("Dashboard", "lakeDashboard", "access") },
      ],
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: <FaUsersCog />,
      open: adminOpen,
      setOpen: setAdminOpen,
      permission: !isContractor && (can("Admin", "userPermissions", "read") || can("Admin", "users", "read")),
      children: [
        { label: 'Users', path: '/users', permission: can("Admin", "users", "read") },
        { label: 'Roles & Permissions', path: '/roles', permission: can("Admin", "roles", "read") },
        { label: 'Departments', path: '/departments', permission: can("Admin", "departments", "read") },
      ],
    },
    {
      id: 'sites',
      label: 'Sites',
      icon: <FaGlobe />,
      open: siteOpen,
      setOpen: setSiteOpen,
      permission: !isContractor,
      children: [
        { label: 'Sites', path: '/sites', permission: true },
      ],
    },
    {
      id: 'vendors',
      label: 'Vendors',
      icon: <FaFileContract />,
      open: contractOpen,
      setOpen: setContractOpen,
      permission: !isContractor,
      children: [
        { label: 'Vendors', path: '/contractor', permission: true },
        { label: 'Agreements', path: '/agreement', permission: true },
        { label: 'Contract Type', path: '/contracttype', permission: true },
      ],
    },
    {
      id: 'tasks',
      label: 'Tasks',
      icon: <FaTasks />,
      open: taskOpen,
      setOpen: setTaskOpen,
      permission: true,
      children: [
        { label: 'Tasks', path: '/taskmanagement', permission: true },
        { label: 'Maintenance', path: '/maintenance', permission: !isContractor },
        { label: 'Workflows', path: '/workflows', permission: !isContractor && can('Workflow', 'workflowTemplates', 'read') },
        { label: 'SLA', path: '/sla', permission: !isContractor },
      ],
    },
    {
      id: 'divider',
      type: 'divider',
      permission: !isContractor,
    },
    {
      id: 'aiintelligence',
      label: 'AI Intelligence',
      icon: <FaBrain />,
      open: ecoAssetOpen,
      setOpen: setEcoAssetOpen,
      permission: !isContractor && (can("AI Intelligence", "ecoAsset", "access") || can("AI Intelligence", "lakeManagement", "access")),
      children: [
        { label: 'Eco Assets', path: '/ecological', permission: can("AI Intelligence", "ecoAsset", "access") },
        { label: 'Lake Management', path: '/lake', permission: can("AI Intelligence", "lakeManagement", "access") },
        { label: 'Upload Trees', path: '/upload-trees', permission: isOrgAdmin() },
      ],
    },
  ];

  // Helper function to get ListItemButton sx styles
  const getListItemButtonSx = (active, nested = false, hasChildren = false) => ({
    borderRadius: 1.5,
    m: 0.5,
    mx: 1,
    pl: nested ? 4 : 2,
    transition: 'all 0.2s ease',
    // Clean white background for parent menu items - no dull grey
    ...(hasChildren && !nested && !active && {
      bgcolor: 'transparent',
      borderLeft: '3px solid transparent',
    }),
    ...(active && {
      bgcolor: 'linear-gradient(135deg, #47638F 0%, #37507A 100%)',
      background: 'linear-gradient(135deg, #47638F 0%, #37507A 100%)',
      color: '#ffffff',
      boxShadow: '0 2px 8px rgba(108, 142, 191, 0.25)',
      '&:hover': {
        bgcolor: 'linear-gradient(135deg, #37507A 0%, #0d47a1 100%)',
        background: 'linear-gradient(135deg, #37507A 0%, #0d47a1 100%)',
        color: '#ffffff',
        boxShadow: '0 4px 12px rgba(108, 142, 191, 0.35)',
      },
      '& .MuiListItemIcon-root': {
        color: '#ffffff',
      },
    }),
    ...(!active && {
      '&:hover': {
        bgcolor: hasChildren ? 'rgba(108, 142, 191, 0.08)' : 'rgba(108, 142, 191, 0.06)',
        transform: 'translateX(4px)',
        color: 'primary.main',
        '& .MuiListItemIcon-root': {
          color: 'primary.main',
        },
      },
    }),
  });

  const renderMenuItem = (item) => {
    if (!item.permission) return null;

    // Handle divider
    if (item.type === 'divider') {
      return <Divider key={item.id} sx={{ my: 1, mx: 1 }} />;
    }

    const hasChildren = item.children && item.children.length > 0;
    const isActive = item.path && location.pathname === item.path;

    if (hasChildren) {
      return (
        <React.Fragment key={item.id}>
          <Tooltip 
            title={isCollapsed ? item.label : ''} 
            placement="right"
            disableHoverListener={!isCollapsed}
          >
            <ListItemButton 
              onClick={() => !isCollapsed && item.setOpen(!item.open)}
              onMouseEnter={(e) => handlePopoverOpen(e, item)}
              sx={getListItemButtonSx(false, false, true)}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              {!isCollapsed && (
                <>
                  <ListItemText 
                    primary={item.label} 
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  {item.open ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>
          </Tooltip>
          {!isCollapsed && (
            <Collapse in={item.open} timeout="auto" unmountOnExit>
              <List 
                component="div" 
                disablePadding
                sx={{
                  bgcolor: 'rgba(108, 142, 191, 0.03)',
                  borderLeft: '3px solid #47638F',
                  ml: 2,
                  mr: 1,
                  borderRadius: 1,
                  py: 0.5,
                  mb: 1,
                }}
              >
                {item.children.map((child) => {
                  if (!child.permission) return null;
                  const childActive = location.pathname === child.path;
                  return (
                    <ListItemButton
                      key={child.path}
                      component={Link}
                      to={child.path}
                      sx={getListItemButtonSx(childActive, true)}
                      onClick={() => isMobile && onClose?.()}
                    >
                      <ListItemText 
                        primary={child.label}
                        primaryTypographyProps={{ fontSize: '0.9rem' }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    }

    return (
      <Tooltip key={item.id} title={isCollapsed ? item.label : ''} placement="right">
        <ListItemButton
          component={Link}
          to={item.path}
          sx={getListItemButtonSx(isActive)}
          onClick={() => isMobile && onClose?.()}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          {!isCollapsed && <ListItemText primary={item.label} />}
        </ListItemButton>
      </Tooltip>
    );
  };

  const drawerContent = (
    <>
      {/* Logo and Toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          minHeight: 70,
          borderBottom: '1px solid rgba(108, 142, 191, 0.12)',
        }}
      >
        {!isCollapsed ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <img
              src="/assets/images/image1.png"
              alt="Encode Nature Icon"
              style={{ height: '44px', width: 'auto' }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #6C8EBF 0%, #6BA368 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: '1.15rem',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
              }}
            >
              Encode Nature
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
            <img
              src="/assets/images/image1.png"
              alt="Encode Nature Icon"
              style={{ height: '32px', width: '32px', objectFit: 'contain', flexShrink: 0 }}
            />
          </Box>
        )}
        {!isMobile && !isCollapsed && (
          <IconButton onClick={toggleSidebar} size="small" sx={{ flexShrink: 0 }}>
            <ChevronLeft />
          </IconButton>
        )}
      </Box>
      
      {/* Collapse toggle for collapsed state */}
      {!isMobile && isCollapsed && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <IconButton onClick={toggleSidebar} size="small">
            <ChevronRight />
          </IconButton>
        </Box>
      )}

      {/* Organization Selector */}
      {!isCollapsed && selectedOrgName && (
        <Box
          onClick={isSuperAdmin ? handleOrgMenuOpen : undefined}
          sx={{
            p: 2,
            mb: 1,
            background: 'linear-gradient(135deg, rgba(108, 142, 191, 0.1) 0%, rgba(71, 99, 143, 0.05) 100%)',
            borderRadius: 2,
            m: 1,
            border: '1px solid rgba(108, 142, 191, 0.15)',
            cursor: isSuperAdmin ? 'pointer' : 'default',
            transition: 'all 0.2s',
            '&:hover': isSuperAdmin ? {
              background: 'linear-gradient(135deg, rgba(108, 142, 191, 0.15) 0%, rgba(71, 99, 143, 0.1) 100%)',
              borderColor: 'rgba(108, 142, 191, 0.3)',
            } : {},
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Organisation Logo */}
            <Avatar
              src={orgLogoUrl}
              sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: 1,
                bgcolor: 'primary.light',
                flexShrink: 0,
              }}
              variant="rounded"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            >
              <FaBuilding size={18} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {isSuperAdmin && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                  Current Organization
                </Typography>
              )}
              <Typography variant="body2" fontWeight={700} noWrap sx={{ color: 'primary.main', mt: isSuperAdmin ? 0.5 : 0 }}>
                {selectedOrgName}
              </Typography>
            </Box>
            {isSuperAdmin ? (
              <SwapHoriz sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }} />
            ) : isOrgAdmin() && (
              <Tooltip title="Edit Organization Logo">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditLogoDialog();
                  }}
                  sx={{ ml: 1 }}
                >
                  <Edit sx={{ fontSize: 16, color: 'primary.main' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {isSuperAdmin && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Click to switch organization
            </Typography>
          )}
        </Box>
      )}
      
      {/* Collapsed Organization Logo */}
      {isCollapsed && selectedOrgId && (
        <Tooltip title={selectedOrgName || 'Organization'} placement="right">
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              py: 1.5,
              cursor: isSuperAdmin ? 'pointer' : 'default',
            }}
            onClick={isSuperAdmin ? handleOrgMenuOpen : undefined}
          >
            <Avatar
              src={orgLogoUrl}
              sx={{ 
                width: 36, 
                height: 36, 
                borderRadius: 1,
                bgcolor: 'primary.light',
              }}
              variant="rounded"
            >
              <FaBuilding size={16} />
            </Avatar>
          </Box>
        </Tooltip>
      )}

      {/* Organization Menu for Super Admin */}
      <Menu
        anchorEl={orgMenuAnchor}
        open={Boolean(orgMenuAnchor)}
        onClose={handleOrgMenuClose}
        PaperProps={{
          sx: {
            maxHeight: 300,
            minWidth: 220,
          }
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Switch Organization
          </Typography>
        </Box>
        {allOrganisations.map((org) => (
          <MenuItem
            key={org.id}
            onClick={() => handleOrgSwitch(org)}
            selected={org.id === selectedOrgId}
            sx={{
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(108, 142, 191, 0.08)',
              }
            }}
          >
            <ListItemIcon>
              <FaBuilding size={16} />
            </ListItemIcon>
            <ListItemText primary={org.name} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            handleOrgMenuClose();
            handleOpenEditLogoDialog();
          }}
          sx={{ py: 1.5 }}
        >
          <ListItemIcon>
            <Edit sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText primary="Edit Organization Logo" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleOrgMenuClose();
            setCreateOrgDialogOpen(true);
          }}
          sx={{ py: 1.5, color: 'primary.main' }}
        >
          <ListItemIcon>
            <Add sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <ListItemText primary="Create New Organization" />
        </MenuItem>
      </Menu>

      {/* Create Organization Dialog */}
      <Dialog
        open={createOrgDialogOpen}
        onClose={() => {
          if (!creatingOrg) {
            setCreateOrgDialogOpen(false);
            setNewOrgLogo(null);
            setLogoPreview(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Create New Organization
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            fullWidth
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            disabled={creatingOrg}
            placeholder="Enter organization name"
            sx={{ mt: 1 }}
          />
          
          {/* Logo Upload Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Organization Logo (Optional)
            </Typography>
            {logoPreview ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <Avatar
                  src={logoPreview}
                  sx={{ width: 64, height: 64, borderRadius: 1 }}
                  variant="rounded"
                />
                <Button
                  size="small"
                  color="error"
                  onClick={handleRemoveLogo}
                  disabled={creatingOrg}
                >
                  Remove
                </Button>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                disabled={creatingOrg}
                sx={{ mt: 1 }}
              >
                Upload Logo
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleLogoChange}
                />
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Maximum file size: 2MB. Supported formats: JPG, PNG, GIF
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setCreateOrgDialogOpen(false);
              setNewOrgLogo(null);
              setLogoPreview(null);
            }}
            disabled={creatingOrg}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateOrg}
            disabled={creatingOrg || !newOrgName.trim()}
            startIcon={creatingOrg ? <CircularProgress size={16} /> : <Add />}
          >
            {creatingOrg ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Organization Logo Dialog */}
      <Dialog
        open={editLogoDialogOpen}
        onClose={() => {
          if (!updatingLogo) {
            setEditLogoDialogOpen(false);
            setEditLogoFile(null);
            setEditLogoPreview(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Edit Organization Logo
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update or remove the logo for <strong>{selectedOrgName}</strong>
          </Typography>
          
          {/* Current Logo */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Current Logo
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Avatar
                src={currentOrgLogo}
                sx={{ width: 64, height: 64, borderRadius: 1, bgcolor: 'grey.200' }}
                variant="rounded"
              >
                <FaBuilding size={24} />
              </Avatar>
              <Typography variant="caption" color="text.secondary">
                {currentOrgLogo ? 'Logo is set' : 'No logo set'}
              </Typography>
            </Box>
          </Box>

          {/* New Logo Upload */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Upload New Logo
            </Typography>
            {editLogoPreview ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <Avatar
                  src={editLogoPreview}
                  sx={{ width: 64, height: 64, borderRadius: 1 }}
                  variant="rounded"
                />
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setEditLogoFile(null);
                    setEditLogoPreview(null);
                  }}
                  disabled={updatingLogo}
                >
                  Remove
                </Button>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                disabled={updatingLogo}
                sx={{ mt: 1 }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleEditLogoChange}
                />
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Maximum file size: 2MB. Supported formats: JPG, PNG, GIF
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button
            color="error"
            onClick={() => handleUpdateLogo(true)}
            disabled={updatingLogo || !currentOrgLogo}
          >
            Remove Logo
          </Button>
          <Box>
            <Button
              onClick={() => {
                setEditLogoDialogOpen(false);
                setEditLogoFile(null);
                setEditLogoPreview(null);
              }}
              disabled={updatingLogo}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => handleUpdateLogo(false)}
              disabled={updatingLogo || !editLogoFile}
              startIcon={updatingLogo ? <CircularProgress size={16} /> : <Edit />}
            >
              {updatingLogo ? 'Updating...' : 'Update Logo'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Main Menu Items */}
      <List sx={{ flexGrow: 1, pt: 2, overflowY: 'auto' }}>
        {menuItems.map(renderMenuItem)}
      </List>

      {/* Popover for collapsed sidebar submenu */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          pointerEvents: 'none',
          ml: 0.5,
        }}
        PaperProps={{
          onMouseEnter: () => {
            // Keep popover open when hovering over it
            if (popoverAnchor) {
              setPopoverAnchor(popoverAnchor);
            }
          },
          onMouseLeave: handlePopoverClose,
          sx: {
            pointerEvents: 'auto',
            boxShadow: 3,
            minWidth: 200,
          },
        }}
      >
        {popoverContent && (
          <Paper elevation={0} sx={{ py: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                px: 2,
                py: 1,
                fontWeight: 600,
                color: 'text.secondary',
                bgcolor: 'grey.50',
              }}
            >
              {popoverContent.label}
            </Typography>
            <List disablePadding>
              {popoverContent.children?.map((child) => {
                if (!child.permission) return null;
                const childActive = location.pathname === child.path;
                return (
                  <ListItemButton
                    key={child.path}
                    component={Link}
                    to={child.path}
                    onClick={handlePopoverClose}
                    sx={{
                      py: 1,
                      px: 2,
                      ...(childActive && {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      }),
                    }}
                  >
                    <ListItemText
                      primary={child.label}
                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        )}
      </Popover>

      <Divider />

      {/* Bottom Menu Items */}
      <List>
        <Tooltip title={isCollapsed ? 'Profile' : ''} placement="right">
          <ListItemButton
            component={Link}
            to="/profile"
            sx={getListItemButtonSx(location.pathname === '/profile')}
            onClick={() => isMobile && onClose?.()}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <FaUserCircle />
            </ListItemIcon>
            {!isCollapsed && <ListItemText primary="Profile" />}
          </ListItemButton>
        </Tooltip>

        <Tooltip title={isCollapsed ? 'Logout' : ''} placement="right">
          <ListItemButton 
            onClick={handleLogout}
            sx={getListItemButtonSx(false)}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <FaSignOutAlt />
            </ListItemIcon>
            {!isCollapsed && <ListItemText primary="Logout" />}
          </ListItemButton>
        </Tooltip>
      </List>
    </>
  );

  return (
    <Drawer
      variant={variant}
      open={variant === 'temporary' ? !isCollapsed : true}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better mobile performance
      }}
      sx={{
        width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          borderRight: '1px solid rgba(108, 142, 191, 0.12)',
          transition: (theme) => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

export default ModernSidebar;
